import React, { useState } from "react";
import axios from "axios";
import { getValidToken } from "../../utils/tokenManager";
import AdminSidebar from "components/common/AdminSidebar";
import Input from "../common/Input";
import Button from "../common/Button";
import Select from "../common/SelectInput";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import success from "../../assets/agent/success.svg";
import fail from "../../assets/agent/fail.svg";
import MobileNewClient from "./mobile/MobileNewClient";

const NewClient = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const initialFormState = {
    company: "",
    product: "",
    address: "",
    urlCalendar: "",
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    secondaryEmail: "",
    preferredLanguage: "",
    script: "",
  };

  const preferredLanguages = [
    { value: "es", label: "Spanish" },
    { value: "en", label: "English" },
  ];

  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [id, setId] = useState(0);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });
  const MAX_CHARACTERS = 1000;

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "script") {
      if (value.length <= MAX_CHARACTERS) {
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      setCharCount(value.length);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!form.company.trim()) newErrors.company = t("validation.requiredField");
    if (!form.product.trim()) newErrors.product = t("validation.requiredField");
    if (!form.address.trim()) newErrors.address = t("validation.requiredField");
    if (!form.preferredLanguage) {
      newErrors.preferredLanguage = t("validation.requiredLanguage");
    }
    if (!form.urlCalendar.trim())
      newErrors.urlCalendar = t("validation.requiredField");
    if (!form.contactPerson.trim())
      newErrors.contactPerson = t("validation.requiredField");
    if (!form.contactPhone.trim())
      newErrors.contactPhone = t("validation.requiredField");

    if (!form.contactEmail.trim()) {
      newErrors.contactEmail = t("validation.requiredField");
    } else if (!emailRegex.test(form.contactEmail)) {
      newErrors.contactEmail = t("validation.invalidEmail");
    }

    if (form.secondaryEmail.trim()) {
      const secondaryEmails = form.secondaryEmail
        .split(",")
        .map((e) => e.trim());
      const invalidEmails = secondaryEmails.filter(
        (email) => email !== "" && !emailRegex.test(email),
      );
      if (invalidEmails.length > 0) {
        newErrors.secondaryEmail = t("validation.invalidEmail");
      }
    }

    if (!form.script.trim()) newErrors.script = t("validation.requiredField");
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrorModal(true);
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }
    const clientData = {
      Address: form.address,
      Contact_Email: form.contactEmail,
      Contact_Person: form.contactPerson,
      Contact_Phone: form.contactPhone,
      Dialog: form.script,
      Name: form.company,
      PreferredLanguage: form.preferredLanguage,
      Product: form.product,
      Url_Calendar: form.urlCalendar,
      EmailNotifications: {
        bcc: [],
        cc: form.secondaryEmail
          ? form.secondaryEmail
              .split(",")
              .map((email) => email.trim())
              .filter((email) => email !== "")
          : [],
      },
    };
    const accessToken = await getValidToken();
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_APIBASEURL}/api/clients`,
        clientData,
        config,
      );

      if (response.status === 201 || response.status === 200) {
        setId(response.data.clientId || response.data.Id || response.data.id);
        setSuccessModal(true);
      }
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      const serverData = error.response?.data;
      let errorMessage = t("error.generic");

      if (serverData) {
        // If there's a specific details string (often a stringified dictionary from the backend)
        errorMessage =
          serverData.details || serverData.error || t("error.generic");
      }

      setErrorModal(true);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSuccessModal = () => {
    setSuccessModal(false);
    setForm(initialFormState);
  };

  const handleModal = () => {
    setErrorModal(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
    <div
      className="hidden lg:flex min-h-screen"
      style={{
        background:
          "linear-gradient(-80deg, #051D42 20%, #0C469F 82%, #0D4AA8 100%)",
      }}
    >
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div
        className={`flex-1 flex flex-col justify-center px-4 sm:px-8 py-6 md:py-7 mt-10 transition-all duration-300 ${
          sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}
      >
        <div className="w-full max-w-6xl mx-auto">
          <h1 className="text-[30px] font-bold text-white">
            {t("client.newClient")}
          </h1>
          <p className="text-white mb-2 md:text-lg md:w-[735px]">
            {t("client.description")}
          </p>
          <div className="relative flex items-center mb-8">
            <span className="absolute left-0 w-1 h-1 bg-white rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-white" />
            <span className="absolute right-0 w-1 h-1 bg-white rounded-full -mr-1"></span>
          </div>
          <form onSubmit={handleSubmit} noValidate>
            <div className="flex justify-end">
              <Select
                label={t("client.preferredLangauge")}
                name="preferredLanguage"
                value={form.preferredLanguage}
                onChange={handleChange}
                error={errors.preferredLanguage}
                options={preferredLanguages}
                required
                labelClassName="text-white"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2 rounded-xl">
              <div>
                <Input
                  label={t("client.company")}
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  error={errors.company}
                  required
                />
              </div>
              <div>
                <Input
                  label={t("client.product")}
                  name="product"
                  value={form.product}
                  onChange={handleChange}
                  error={errors.product}
                  required
                />
              </div>
              <div>
                <Input
                  label={t("client.address")}
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  error={errors.address}
                  required
                />
              </div>
              <div>
                <Input
                  label={t("client.urlCalendar")}
                  name="urlCalendar"
                  value={form.urlCalendar}
                  onChange={handleChange}
                  error={errors.urlCalendar}
                  required
                />
              </div>
              <div>
                <Input
                  label={t("client.contactPerson")}
                  name="contactPerson"
                  value={form.contactPerson}
                  onChange={handleChange}
                  error={errors.contactPerson}
                  required
                />
              </div>
              <div>
                <Input
                  label={t("client.contactPhone")}
                  name="contactPhone"
                  value={form.contactPhone}
                  onChange={handleChange}
                  error={errors.contactPhone}
                  required
                />
              </div>
              <div>
                <Input
                  label={t("client.contactEmail")}
                  name="contactEmail"
                  value={form.contactEmail}
                  onChange={handleChange}
                  error={errors.contactEmail}
                  required
                />
                <Input
                  label={t("client.secondaryEmail")}
                  name="secondaryEmail"
                  value={form.secondaryEmail}
                  onChange={handleChange}
                  error={errors.secondaryEmail}
                />
              </div>
              <div>
                <label
                  htmlFor="script"
                  className="block text-white text-base font-medium mb-1"
                >
                  {t("client.script")}
                </label>
                <textarea
                  id="script"
                  className="w-full h-32 p-3 rounded bg-[#223A5F] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  name="script"
                  value={form.script}
                  onChange={handleChange}
                  required
                />
                <div
                  className={`mt-1 text-sm ${
                    charCount > MAX_CHARACTERS
                      ? "text-red-500"
                      : "text-gray-300"
                  }`}
                >
                  {charCount}/{MAX_CHARACTERS} {t("characters")}
                </div>
                {charCount > MAX_CHARACTERS && (
                  <div className="text-red-500 text-sm">
                    {t("validation.maxCharacters", { max: MAX_CHARACTERS })}
                  </div>
                )}
              </div>

              <div className="md:col-span-2 flex justify-between items-center mt-4">
                <button
                  type="button"
                  className="text-white text-2xl font-semibold"
                  onClick={handleBack}
                >
                  {t("back")}
                </button>
                <Button
                  variant="primary"
                  type="submit"
                  className="w-40"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("button.loading") : t("button.register")}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userConfirm.clientHead")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl text-secondary font-bold">
              {form.company} {t("admin.userConfirm.with")} {id}
            </p>
            <p className="mb-6 text-xl">{t("admin.userConfirm.descClient")}</p>
            <Link to="/dashboard">
              <Button variant="primary" onClick={closeSuccessModal}>
                {t("admin.userConfirm.btn")}
              </Button>
            </Link>
          </div>
        </div>
      )}
      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userError.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            {errors.submit && (
              <p className="mb-6 text-xl w-[455px] text-center">
                {errors.submit}
              </p>
            )}

            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
      {errors.submit}
    </div>
    <div className="lg:hidden">
      <MobileNewClient />
    </div>
    </>
  );
};

export default NewClient;
